import React from 'react'
import styled from 'styled-components'
import Visualization from '~/packages/sentinel/visualization/visualization.component'
import insightReadDefinition from '~/packages/insight/read/insightRead.definition'
import useAction from '~/prix/react/hooks/action'
import { getAlertColor, getAlertLabel, getAlertTextColor } from '~/packages/variable/spaceTime.data'
import { normalizeText } from '~/utils/shared/insightText'
import { isUuid } from '~/utils/shared/isUuid'
import { Skeleton } from '../skeletonLoading'
import WelcomeCard from '~/packages/welcome/components/welcomeCard.component'

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;
  margin-top: 10px;
`

const welcomePageCardDisplayDelayMilliseconds = 650

const InsightCard = styled.div<{ $isWelcomePage?: boolean; $fadeIn?: boolean }>`
  background: #ffffff;
  border-radius: ${({ $isWelcomePage }) => ($isWelcomePage ? '8px' : '12px')};
  padding: ${({ $isWelcomePage }) => ($isWelcomePage ? '16px' : '12px')};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  border: ${({ $isWelcomePage }) => ($isWelcomePage ? '0' : '1px solid rgba(0, 0, 0, 0.06)')};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: ${({ $isWelcomePage }) => ($isWelcomePage ? 'space-between' : 'flex-start')};

  //animação de fade in com keyframe
  @keyframes fadeIn {
    0% {
      opacity: 0;
      transform: scale(0.9);
    }
    65% {
      transform: scale(1.025);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  ${({ $fadeIn }) =>
    $fadeIn
      ? `
    animation: fadeIn 300ms ease-out;
  `
      : ''}
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
`

const CardTitle = styled.div<{ $isWelcomePage?: boolean }>`
  font-size: ${({ $isWelcomePage }) => ($isWelcomePage ? '18px' : '14px')};
  font-weight: 700;
  color: #1f2937;
  flex: 1;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    font-size: ${({ $isWelcomePage }) => ($isWelcomePage ? '16px' : '14px')};
  }
`

const CardAlert = styled.small<{ $alert: any }>`
  padding: 0.3rem 0.8rem;
  border-radius: 8px;
  align-self: flex-start;
  text-transform: uppercase;
  font-weight: 700;
  background-color: ${({ $alert }) => getAlertColor($alert)};
  color: ${({ $alert }) => getAlertTextColor($alert)};
  white-space: nowrap;
`

const CardMeta = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
`

const CardDescription = styled.div<{ $isWelcomePage?: boolean }>`
  font-size: ${({ $isWelcomePage }) => ($isWelcomePage ? '16px' : '12px')};
  color: #374151;
  line-height: ${({ $isWelcomePage }) => ($isWelcomePage ? 1.15 : 1.4)};
  margin-bottom: 10px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    font-size: ${({ $isWelcomePage }) => ($isWelcomePage ? '14px' : '12px')};
  }
`

function toCleanStringList(value: unknown): string[] {
  if (!value) return []

  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === 'string')
      .map(item => item.trim())
      .filter(Boolean)
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? [trimmed] : []
  }

  return []
}

function splitPortfolioName(value: unknown): string[] {
  if (!value) return []
  if (Array.isArray(value)) return toCleanStringList(value)
  if (typeof value !== 'string') return []

  return value
    .split(';')
    .map(item => item.trim())
    .filter(Boolean)
}

function buildMetaLine(parts: unknown[]): string {
  const seen = new Set<string>()
  const unique: string[] = []

  for (const part of parts) {
    for (const item of toCleanStringList(part)) {
      const normalized = normalizeText(item)
      if (!normalized) continue
      if (seen.has(normalized)) continue
      seen.add(normalized)
      unique.push(item)
    }
  }

  return unique.join(' • ')
}

function InsightCardFromId({
  insightId,
  isWelcomePage,
  index,
}: {
  insightId: string
  isWelcomePage?: boolean
  index: number
}) {
  if (!isUuid(insightId)) return null

  const [isReady, setIsReady] = React.useState(false)

  const insightReadResult = useAction(insightReadDefinition, { id: insightId }, [insightId], {
    staleWhileRevalidate: true,
  })

  React.useEffect(() => {
    if (insightReadResult.isLoading && !insightReadResult.result) {
      return
    }

    if (!isWelcomePage) {
      setIsReady(true)
      return
    }

    const timeout = setTimeout(() => {
      setIsReady(true)
    }, index * welcomePageCardDisplayDelayMilliseconds)

    return () => clearTimeout(timeout)
  }, [index, isWelcomePage, insightReadResult.isLoading, insightReadResult.result])

  if (!isReady) {
    return (
      <InsightCard $isWelcomePage={isWelcomePage}>
        <Skeleton height={18} width='70%' borderRadius={6} style={{ marginBottom: '10px' }} />
        <Skeleton height={15} width='50%' borderRadius={6} style={{ marginBottom: '8px' }} />
        <Skeleton height={15} width='100%' borderRadius={6} style={{ marginBottom: '6px' }} />
        <Skeleton height={15} width='94%' borderRadius={6} style={{ marginBottom: '6px' }} />
        <Skeleton height={25} width='100%' borderRadius={8} style={{ flex: 1 }} />
      </InsightCard>
    )
  }

  if (insightReadResult.error) {
    return (
      <InsightCard $isWelcomePage={isWelcomePage}>
        Não foi possível carregar este insight: {insightReadResult.error.message}
      </InsightCard>
    )
  }

  const insight = (insightReadResult.result as any)?.insight
  if (!insight) {
    return null
  }

  const visualizations = Array.isArray(insight.visualizations) ? insight.visualizations : []

  const meta = buildMetaLine([
    insight.delimitationTitle,
    insight.variableTitle,
    insight.variableLabel,
    insight.productName,
    splitPortfolioName(insight.portfolioName),
  ])

  return (
    <InsightCard $isWelcomePage={isWelcomePage} $fadeIn={isReady}>
      <div>
        <CardHeader>
          <CardTitle $isWelcomePage={isWelcomePage}>{insight.title}</CardTitle>
          {insight.alert ? (
            <CardAlert $alert={insight.alert}>{getAlertLabel(insight.alert)}</CardAlert>
          ) : null}
        </CardHeader>

        {meta ? (
          <CardMeta style={isWelcomePage ? { fontSize: '14px' } : undefined}>{meta}</CardMeta>
        ) : null}
        {insight.description ? (
          <CardDescription $isWelcomePage={isWelcomePage}>{insight.description}</CardDescription>
        ) : null}

        {visualizations.length === 0 ? (
          <CardMeta
            style={{
              textAlign: 'center',
              flex: 1,
              color: '#9ca3af',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 0,
            }}
          >
            Sem visualizações disponíveis para este insight.
          </CardMeta>
        ) : null}
      </div>

      {visualizations.map((visualization: any, visualizationIndex: number) => (
        <Visualization
          key={visualizationIndex}
          visualization={visualization}
          isExpanded={false}
          insightTitle={insight.title}
          alert={insight.alert ?? null}
          animated={isWelcomePage}
          animationDelay={visualizationIndex * 450 + 250}
        />
      ))}
    </InsightCard>
  )
}

export default function ShowInsightCards({
  insightIds,
  isWelcomePage = false,
}: {
  insightIds: string[]
  isWelcomePage?: boolean
}) {
  const safeInsightIds = Array.from(new Set(insightIds.filter(isUuid)))
  if (!safeInsightIds.length) return null

  const CardsContainer = !isWelcomePage ? CardsGrid : React.Fragment

  return (
    <CardsContainer>
      {safeInsightIds.map((insightId, index) => (
        <InsightCardFromId
          key={insightId}
          insightId={insightId}
          isWelcomePage={isWelcomePage}
          index={index}
        />
      ))}
    </CardsContainer>
  )
}
